import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./api/authentication/guards/auth.guard";
import { ClientActivityComponent } from "./components/dashboard/clients/client-activity/client-activity.component";
import { ClientFundingRequestComponent } from "./components/dashboard/clients/client-funding-request/client-funding-request.component";
import { ClientFundingRequestsComponent } from "./components/dashboard/clients/client-funding-requests/client-funding-requests.component";
import { ClientAiUsageComponent } from "./components/dashboard/clients/client-ai-usage/client-ai-usage.component";
import { ClientOrdersComponent } from "./components/dashboard/clients/client-orders/client-orders.component";
import { ClientUsersComponent } from "./components/dashboard/clients/client-users/client-users.component";
import { ClientComponent } from "./components/dashboard/clients/client/client.component";
import { ClientsComponent } from "./components/dashboard/clients/index/clients.component";
import { AdCampaignsComponent } from "./components/dashboard/cms/ad-campaigns/ad-campaigns.component";
import { AdsWrapperComponent } from "./components/dashboard/cms/ads-wrapper/ads-wrapper.component";
import { AdsComponent } from "./components/dashboard/cms/ads/ads.component";
import { CategoriesComponent } from "./components/dashboard/cms/categories/categories.component";
import { DocumentationStandardsComponent } from "./components/dashboard/cms/documentation-standards/documentation-standards.component";
import { CmsComponent } from "./components/dashboard/cms/index/cms.component";
import { CommissionsBilledComponent } from "./components/dashboard/commissions/billed/commissions-billed.component";
import { CommissionsListComponent } from "./components/dashboard/commissions/list/commissions-list.component";
import { CommissionsRequestsComponent } from "./components/dashboard/commissions/requests/commissions-requests.component";
import { DashboardComponent } from "./components/dashboard/dashboard/dashboard.component";
import { ExpatActivityComponent } from "./components/dashboard/expats/expat-activity/expat-activity.component";
import { ExpatDetailsComponent } from "./components/dashboard/expats/expat-details/expat-details.component";
import { ExpatComponent } from "./components/dashboard/expats/expat/expat.component";
import { ExpatsComponent } from "./components/dashboard/expats/expats.component";
import { InvoiceComponent } from "./components/dashboard/invoices/invoice/invoice.component";
import { InvoicesListComponent } from "./components/dashboard/invoices/list/invoices-list.component";
import { InvoicesLogComponent } from "./components/dashboard/invoices/log/invoices-log.component";
import { OrderActivityComponent } from "./components/dashboard/orders/order-activity/order-activity.component";
import { OrderDetailsComponent } from "./components/dashboard/orders/order-details/order-details.component";
import { OrderDocumentsComponent } from "./components/dashboard/orders/order-documents/order-documents.component";
import { OrderInvoicesComponent } from "./components/dashboard/orders/order-invoices/order-invoices.component";
import { OrderComponent } from "./components/dashboard/orders/order/order.component";
import { OrdersComponent } from "./components/dashboard/orders/orders.component";
import { ClientPlansComponent } from "./components/dashboard/pricing/client-plans/client-plans.component";
import { PricingComponent } from "./components/dashboard/pricing/pricing/pricing.component";
import { ProviderPlansComponent } from "./components/dashboard/pricing/provider-plans/provider-plans.component";
import { CommissionComponent } from "./components/dashboard/providers/commission/commission.component";
import { ProvidersComponent } from "./components/dashboard/providers/index/providers.component";
import { ProviderActivityComponent } from "./components/dashboard/providers/provider-activity/provider-activity.component";
import { ProviderCategoriesComponent } from "./components/dashboard/providers/provider-categories/provider-categories.component";
import { ProviderDocumentsComponent } from "./components/dashboard/providers/provider-documents/provider-documents.component";
import { ProviderOffersComponent } from "./components/dashboard/providers/provider-offers/provider-offers.component";
import { ProviderAiUsageComponent } from "./components/dashboard/providers/provider-ai-usage/provider-ai-usage.component";
import { ProviderOrdersComponent } from "./components/dashboard/providers/provider-orders/provider-orders.component";
import { ProviderUsersComponent } from "./components/dashboard/providers/provider-users/provider-users.component";
import { ProviderComponent } from "./components/dashboard/providers/provider/provider.component";
import { UserAgreementsComponent } from "./components/dashboard/providers/user-agreements/user-agreements.component";
import { AddServiceOfferComponent } from "./components/dashboard/service-offers/add-service-offer/add-service-offer.component";
import { AssociatedOffersComponent } from "./components/dashboard/service-offers/associated-offers/associated-offers.component";
import { AddBundleComponent } from "./components/dashboard/service-offers/bundles/add-bundle.component";
import { OfferActivityComponent } from "./components/dashboard/service-offers/offer-activity/offer-activity.component";
import { ReviewsOffersComponent } from "./components/dashboard/service-offers/reviews-offers/reviews-offers.component";
import { ServiceOfferComponent } from "./components/dashboard/service-offers/service-offer/service-offer.component";
import { ServiceOfferDetailsComponent } from "./components/dashboard/service-offers/service-offers-details/service-offers-details.component";
import { ServiceOffersListComponent } from "./components/dashboard/service-offers/service-offers-list/service-offers-list.component";
import { ServiceOffersComponent } from "./components/dashboard/service-offers/service-offers.component";
import { CompanyUserDetailsComponent } from "./components/dashboard/users/company-user-details/company-user-details.component";
import { UserComponent } from "./components/dashboard/users/user/user.component";
import { UsersListComponent } from "./components/dashboard/users/users-list/users-list.component";
import { NotFoundComponent } from "./components/not-found/not-found.component";
import { RFQSComponent } from "./components/RFQS/index/rfqs.component";
import { QuotationsComponent } from "./components/RFQS/quotations/quotations.component";
import { RFQDetailsComponent } from "./components/RFQS/rfq-details/rfq-details.component";
import { RFQHistoryComponent } from "./components/RFQS/rfq-history/rfq-history.component";
import { RFQComponent } from "./components/RFQS/rfq/rfq.component";
import { DashboardLayoutComponent } from "./layout/dashboard-layout/dashboard-layout.component";
import { CompanyDetailsComponent } from "./modules/company/company-details/company-details.component";
import { entityGuard } from "@api/account/guards/entity.guard";

const routes: Routes = [
  // redirect
  { path: "", redirectTo: "/dashboard", pathMatch: "full" },
  { path: "cms", redirectTo: "/cms/service-categories", pathMatch: "full" },
  { path: "cms/ads", redirectTo: "/cms/ads/pricing", pathMatch: "full" },
  {
    path: "service-offers",
    redirectTo: "/service-offers/list",
    pathMatch: "full",
  },
  // dashboard routes
  {
    path: "",
    component: DashboardLayoutComponent,
    canActivate: [AuthGuard, entityGuard],
    children: [
      {
        path: "dashboard",
        loadChildren: () =>
          import("./modules/dashboard/dashboard.module").then(
            (m) => m.DashboardModule
          ),
      },
      // subscriptions route
      {
        path: "financial/subscribers",
        loadChildren: () =>
          import("./modules/subscriptions/subscriptions.module").then(
            (m) => m.SubscriptionsModule
          ),
      },
      {
        path: "account",
        loadChildren: () =>
          import("./modules/account/account.module").then(
            (m) => m.AccountModule
          ),
      },
      {
        path: "dashboard-old",
        component: DashboardComponent,
      },
      {
        path: "admins",
        loadChildren: () =>
          import("./modules/admin/admin.module").then((m) => m.AdminModule),
      },
      {
        path: "artifacts",
        loadChildren: () =>
          import("./modules/artifacts/artifacts.module").then(
            (m) => m.ArtifactsModule
          ),
      },
      {
        path: "notifications",
        loadChildren: () =>
          import("./modules/notifications/notifications.module").then(
            (m) => m.NotificationsModule
          ),
      },
      {
        path: "settings",
        loadChildren: () =>
          import("./modules/settings/settings.module").then(
            (m) => m.SettingsModule
          ),
      },
      {
        path: "pricing",
        component: PricingComponent,
        children: [
          {
            path: "client",
            component: ClientPlansComponent,
          },
          {
            path: "provider",
            component: ProviderPlansComponent,
          },
        ],
      },
      {
        path: "providers",
        component: ProvidersComponent,
      },
      {
        path: "providers/:id",
        component: ProviderComponent,
        children: [
          {
            path: "",
            component: CompanyDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "users",
            component: ProviderUsersComponent,
          },
          {
            path: "categories",
            component: ProviderCategoriesComponent,
          },
          {
            path: "offers",
            component: ProviderOffersComponent,
          },
          {
            path: "orders",
            component: ProviderOrdersComponent,
          },
          {
            path: "ai-usage",
            component: ProviderAiUsageComponent,
          },
          {
            path: "history",
            component: ProviderActivityComponent,
          },
          {
            path: "docs",
            component: UserAgreementsComponent,
          },
          {
            path: "agreements",
            component: ProviderDocumentsComponent,
          },
          {
            path: "commission",
            component: CommissionComponent,
          },
        ],
      },
      {
        path: "commissions",
        children: [
          {
            path: "custom",
            component: CommissionsListComponent,
          },
          {
            path: "approve",
            component: CommissionsRequestsComponent,
          },
        ],
      },
      {
        path: "financial",
        children: [
          {
            path: "invoices",
            component: InvoicesListComponent,
          },
          {
            path: "invoices/:id",
            component: InvoiceComponent,
          },
          {
            path: "fees",
            component: CommissionsBilledComponent,
          },
          {
            path: "history",
            component: InvoicesLogComponent,
          },
        ],
      },
      {
        path: "clients",
        component: ClientsComponent,
      },
      {
        path: "clients/:id",
        component: ClientComponent,
        children: [
          {
            path: "",
            component: CompanyDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "users",
            component: ClientUsersComponent,
          },
          {
            path: "orders",
            component: ClientOrdersComponent,
          },
          {
            path: "ai-usage",
            component: ClientAiUsageComponent,
          },
          {
            path: "funding-requests/:fundingRequestId",
            component: ClientFundingRequestComponent,
          },
          {
            path: "funding-requests",
            component: ClientFundingRequestsComponent,
          },
          {
            path: "history",
            component: ClientActivityComponent,
          },
        ],
      },
      {
        path: "rfqs-bids",
        component: RFQSComponent,
      },
      {
        path: "rfqs-bids/:id",
        component: RFQComponent,
        children: [
          {
            path: "",
            component: RFQDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "quotations",
            component: QuotationsComponent,
          },
          {
            path: "history",
            component: RFQHistoryComponent,
          },
        ],
      },
      {
        path: "service-offers",
        component: ServiceOffersComponent,
        children: [
          {
            path: "list",
            component: ServiceOffersListComponent,
          },
          {
            path: "new",
            component: AddServiceOfferComponent,
          },
          {
            path: "bundle",
            component: AddBundleComponent,
          },
          {
            path: ":id/bundle",
            component: AddBundleComponent,
          },
          {
            path: ":id/edit",
            component: AddServiceOfferComponent,
          },
        ],
      },
      {
        path: "service-offers/:id",
        component: ServiceOfferComponent,
        children: [
          {
            path: "",
            component: ServiceOfferDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "associated-offers",
            component: AssociatedOffersComponent,
          },
          {
            path: "reviews-offers",
            component: ReviewsOffersComponent,
          },
          {
            path: "history",
            component: OfferActivityComponent,
          },
          // {
          //   path: 'history',
          //   component: OfferHistoryComponent
          // },
        ],
      },
      {
        path: "orders",
        component: OrdersComponent,
      },
      {
        path: "orders/:id",
        component: OrderComponent,
        children: [
          {
            path: "",
            component: OrderDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "docs",
            component: OrderDocumentsComponent,
          },
          {
            path: "invoices",
            component: OrderInvoicesComponent,
          },
          {
            path: "history",
            component: OrderActivityComponent,
          },
        ],
      },
      {
        path: "expats",
        component: ExpatsComponent,
      },
      {
        path: "expats/:id",
        component: ExpatComponent,
        children: [
          {
            path: "",
            component: ExpatDetailsComponent,
            data: { pathName: "Details" },
          },
          {
            path: "history",
            component: ExpatActivityComponent,
          },
        ],
      },
      {
        path: "users",
        component: UsersListComponent,
      },
      {
        path: "users/:id",
        component: UserComponent,
        children: [
          {
            path: "",
            component: CompanyUserDetailsComponent,
            data: { pathName: "Details" },
          },
        ],
      },

      {
        path: "cms",
        component: CmsComponent,
        children: [
          {
            path: "service-categories",
            component: CategoriesComponent,
          },
          {
            path: ":service_category_id/documentation-standards",
            component: DocumentationStandardsComponent,
          },
          {
            path: "ads",
            component: AdsWrapperComponent,
            children: [
              {
                path: "pricing",
                component: AdsComponent,
              },
              {
                path: "ad-campaigns",
                component: AdCampaignsComponent,
              },
            ],
          },
          {
            path: "tax-rates",
            loadChildren: () =>
              import("./modules/tax/tax.module").then((m) => m.TaxModule),
          },
        ],
      },
      {
        path: "settings",
        loadChildren: () =>
          import("./modules/settings/settings.module").then(
            (m) => m.SettingsModule
          ),
      },
    ],
  },

  // not found
  {
    path: "",
    component: DashboardLayoutComponent,
    canActivate: [AuthGuard, entityGuard],
    children: [
      {
        path: "**",
        component: NotFoundComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: "always",
      anchorScrolling: "enabled",
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
